import React from 'react'
import { FormProvider, useForm, useFieldArray } from 'react-hook-form'
import { CreateVolunteersFormType, SingleVolunteerEntryType, NO_EMAIL_AVAILABLE_OPTION } from '../../schemas/volunteers.schema'
import { zodResolver } from '@hookform/resolvers/zod'
import { createVolunteersFormSchema } from '../../schemas/volunteers.schema'
import { createVolunteer, getVolunteerTableRows, addGroupVolunteer, updateUser } from '../../actions/volunteers.actions'
import errorToast from '@/shared/lib/toast/error.toast'
import successToast from '@/shared/lib/toast/success.toast'
import { useQueryClient } from '@tanstack/react-query'
import { ADMIN } from '@/shared/constants/backend-api'
import { USER_WITH_EMAIL_ALREADY_EXISTS_ERROR } from '@/shared/constants/error-messages'
import type { NonHomeownerUser } from '../../types/volunteers.type'
import VolunteerBaseForm from './volunteer-base-form'
import { Button } from '@/shared/components/ui/button'
import { DialogClose } from '@/shared/components/ui/dialog'
import { Plus } from 'lucide-react'
import type { DefaultServePeriod } from '../../components/volunteer-search-combobox'
import { localDateFromApiDateString } from '../../lib/merge-serve-periods-for-form'
import { useRegionId } from '@/shared/hooks/use-region-id'

const BASE_VOLUNTEER_ENTRY: Omit<SingleVolunteerEntryType, 'servePeriods'> = {
    fullName: '',
    email: '',
    phone: '',
    preferredLocation: '',
    organizationName: '',
    dateOfBirth: undefined,
    gender: undefined,
    profilePicture: undefined,
}

function makeDefaultEntry(
    defaultServePeriod?: DefaultServePeriod,
    fallbackRegionId?: number | null,
    defaultGroupOrganization?: string,
): SingleVolunteerEntryType {
    const resolvedRegionId = defaultServePeriod?.regionId ?? fallbackRegionId ?? undefined
    const orgDefault = defaultGroupOrganization?.trim() ?? ''
    return {
        ...BASE_VOLUNTEER_ENTRY,
        organizationName: orgDefault,
        servePeriods: defaultServePeriod
            ? [{
                from: localDateFromApiDateString(defaultServePeriod.from),
                to: localDateFromApiDateString(defaultServePeriod.to),
                regionId: resolvedRegionId ?? undefined,
            }]
            : [{ from: undefined, to: undefined, regionId: resolvedRegionId ?? undefined }],
    }
}

type CreateVolunteerFormProps = {
    onSuccessCallback?: () => void
    /** Override submit button label (e.g. "Add to Group" when adding from group context) */
    submitButtonLabel?: string
    /** When set, volunteers are added to this group; existing users (by email) are linked only, new users are created then linked */
    groupId?: string
    /** When adding from a group context, pre-fill the first serve period with these dates/region */
    defaultServePeriod?: DefaultServePeriod
    /** Group "Church / Organization" — Add Volunteer only; not used on Update Volunteer */
    defaultGroupOrganization?: string
}


function CreateVolunteerForm({ onSuccessCallback, submitButtonLabel, groupId, defaultServePeriod, defaultGroupOrganization }: CreateVolunteerFormProps) {
    const queryClient = useQueryClient()
    const { regionId: currentRegionId } = useRegionId()
    const emailLower = (s: string) => (s || '').trim().toLowerCase()
    const isNoEmailAvailable = (email: string) => emailLower(email) === emailLower(NO_EMAIL_AVAILABLE_OPTION)
    const buildNoEmailPlaceholder = (fullName: string | undefined, index: number) => {
        const safeName = (fullName || "volunteer")
            .trim()
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, '-')
            .replace(/^-+|-+$/g, '')
            .slice(0, 24) || 'volunteer'
        return `no-email+${safeName}-${Date.now()}-${index}@reliefhub.local`
    }

    const form = useForm<CreateVolunteersFormType>({
        resolver: zodResolver(createVolunteersFormSchema),
        defaultValues: {
            volunteers: [makeDefaultEntry(defaultServePeriod, currentRegionId, defaultGroupOrganization)],
        },
    })
    const { handleSubmit, formState: { errors, isSubmitting } } = form
    const { fields, append, remove } = useFieldArray({
        control: form.control,
        name: 'volunteers',
    })

    React.useEffect(() => {
        if (defaultServePeriod?.regionId != null) return
        if (currentRegionId == null) return
        const volunteers = form.getValues('volunteers') ?? []
        if (volunteers.length === 0) return
        volunteers.forEach((_, index) => {
            const current = form.getValues(`volunteers.${index}.servePeriods.0.regionId` as const)
            if (current == null || String(current).trim() === '') {
                form.setValue(`volunteers.${index}.servePeriods.0.regionId` as const, currentRegionId, {
                    shouldDirty: false,
                    shouldValidate: false,
                })
            }
        })
    }, [currentRegionId, defaultServePeriod?.regionId, form, fields.length])

    const onSubmit = async (data: CreateVolunteersFormType) => {
        if (groupId) {
            // Add to group: existing users (by email) → link only; new users → create then link
            let addedCount = 0
            let newInDbCount = 0
            let firstError: string | null = null
            let firstErrorIndex: number | null = null

            for (let i = 0; i < data.volunteers.length; i++) {
                const v = data.volunteers[i]
                const rawEmail = (v.email || '').trim()
                const wantEmail = emailLower(rawEmail)
                const noEmailSelected = isNoEmailAvailable(rawEmail)
                if (!wantEmail && !noEmailSelected) {
                    form.setError(`volunteers.${i}.email` as any, { message: 'Email is required' }, { shouldFocus: true })
                    if (!firstError) firstError = 'Email is required'
                    continue
                }
                const emailForCreate = noEmailSelected ? buildNoEmailPlaceholder(v.fullName, i) : wantEmail
                const normalizedVolunteer = { ...v, email: emailForCreate }

                try {
                    let existing: NonHomeownerUser | undefined
                    if (!noEmailSelected) {
                        const searchRes = await getVolunteerTableRows({ search: wantEmail, page: 1, limit: 20 })
                        existing = searchRes.success && searchRes.data?.users
                            ? (searchRes.data.users as NonHomeownerUser[]).find((u) => emailLower(u.email) === wantEmail)
                            : undefined
                    }

                    if (existing) {
                        const addRes = await addGroupVolunteer(groupId, existing.id)
                        if (!addRes.success) throw new Error(addRes.message ?? 'Failed to add to group')
                        // If the form has valid serve periods, persist them to the existing user's profile.
                        // The search combobox auto-fills existing serve periods, and the user may have
                        // added new ones — sending the full list replaces serveDates with the merged set.
                        const validServePeriods = (normalizedVolunteer.servePeriods ?? []).filter(
                            (p) => p?.from && p?.to,
                        )
                        if (validServePeriods.length > 0) {
                            const up = await updateUser(existing.id, { servePeriods: validServePeriods } as any)
                            if (!up.success) throw new Error(up.message ?? 'Failed to save serve dates')
                        }
                        addedCount++
                    } else {
                        const createPayload: Record<string, unknown> = {
                            ...normalizedVolunteer,
                            role: 4,
                            groupId,
                        }
                        const periodRegion = defaultServePeriod?.regionId
                        const periodRegionNum =
                            typeof periodRegion === 'number'
                                ? periodRegion
                                : periodRegion != null && String(periodRegion).trim() !== ''
                                  ? parseInt(String(periodRegion), 10)
                                  : NaN
                        const effectiveUrlRegion =
                            currentRegionId ??
                            (!Number.isNaN(periodRegionNum) ? periodRegionNum : null)
                        if (effectiveUrlRegion != null) {
                            createPayload.urlRegionId = effectiveUrlRegion
                        }
                        const createRes = await createVolunteer(createPayload as any)
                        if (!createRes.success) {
                            if (createRes.message === USER_WITH_EMAIL_ALREADY_EXISTS_ERROR) {
                                form.setError(
                                    `volunteers.${i}.email` as any,
                                    { message: createRes.message },
                                    { shouldFocus: true },
                                )
                            }
                            if (!firstError) {
                                firstError = createRes.message
                                firstErrorIndex = i
                            }
                            continue
                        }
                        newInDbCount++
                        const newUserId = createRes.data?.user?.id
                        if (newUserId != null) {
                            const addRes = await addGroupVolunteer(groupId, newUserId)
                            if (!addRes.success) throw new Error(addRes.message ?? 'Failed to add to group')
                            // Explicitly persist serve periods after creation.
                            // createVolunteer sends servePeriods via FormData, but the pre-filled
                            // string values (from defaultServePeriod) are not always persisted reliably
                            // through that path. updateUser is the proven path (same fix as existing users).
                            const validServePeriods = (normalizedVolunteer.servePeriods ?? []).filter(
                                (p) => p?.from && p?.to,
                            )
                            if (validServePeriods.length > 0) {
                                const up = await updateUser(newUserId, { servePeriods: validServePeriods } as any)
                                if (!up.success) throw new Error(up.message ?? 'Failed to save serve dates')
                            }
                        }
                        addedCount++
                    }
                } catch (e) {
                    const msg = e instanceof Error ? e.message : 'Failed to add to group'
                    if (!firstError) firstError = msg
                    if (firstErrorIndex == null) firstErrorIndex = i
                }
            }

            if (addedCount > 0) {
                await Promise.all([
                    queryClient.invalidateQueries({ queryKey: [ADMIN.base] }),
                    queryClient.invalidateQueries({ queryKey: [ADMIN.groupVolunteersBase, groupId] }),
                    queryClient.invalidateQueries({ queryKey: [ADMIN.tableRows, 'group', groupId] }),
                    queryClient.invalidateQueries({ queryKey: [ADMIN.tableRows] }),
                ])
                const newMsg = newInDbCount > 0 ? ` (${newInDbCount} new in database)` : ''
                successToast(`${addedCount} volunteer${addedCount === 1 ? '' : 's'} added to group${newMsg}`)
                if (onSuccessCallback) onSuccessCallback()
            }
            if (firstError != null && addedCount === 0) {
                errorToast(firstError)
            }
            return
        }

        // Standalone add: create volunteer only
        let successCount = 0
        let firstError: string | null = null
        let firstErrorIndex: number | null = null

        for (let i = 0; i < data.volunteers.length; i++) {
            const volunteer = data.volunteers[i]
            const rawEmail = (volunteer.email || '').trim()
            const payloadEmail = isNoEmailAvailable(rawEmail)
                ? buildNoEmailPlaceholder(volunteer.fullName, i)
                : emailLower(rawEmail)
            const payload = { ...volunteer, email: payloadEmail, role: 4 }
            const response = await createVolunteer(payload as any)
            if (!response.success) {
                if (response.message === USER_WITH_EMAIL_ALREADY_EXISTS_ERROR) {
                    form.setError(
                        `volunteers.${i}.email` as any,
                        { message: response.message },
                        { shouldFocus: true },
                    )
                }
                if (!firstError) {
                    firstError = response.message
                    firstErrorIndex = i
                }
            } else {
                successCount++
            }
        }

        if (firstError != null && successCount === 0) {
            errorToast(firstError)
            return
        }
        if (successCount > 0) {
            queryClient.invalidateQueries({ queryKey: [ADMIN.base] })
            successToast(
                successCount === data.volunteers.length
                    ? `${successCount} volunteer${successCount === 1 ? '' : 's'} added successfully`
                    : `${successCount} of ${data.volunteers.length} volunteer${data.volunteers.length === 1 ? '' : 's'} added.${firstError ? ` First error: ${firstError}` : ''}`,
            )
            if (onSuccessCallback && successCount === data.volunteers.length) {
                onSuccessCallback()
            } else if (successCount > 0 && successCount < data.volunteers.length && onSuccessCallback) {
                onSuccessCallback()
            }
        }
    }

    return (
        <FormProvider {...form}>
            <form
                onSubmit={handleSubmit(onSubmit)}
                noValidate
            >
                <div className="space-y-8">
                    {fields.map((field, index) => (
                        <div key={field.id} className="space-y-5">
                            {fields.length > 1 && (
                                <div className="flex items-center justify-between gap-2">
                                    <p className="text-sm font-semibold text-foreground">Volunteer {index + 1}</p>
                                    <Button
                                        type="button"
                                        variant="ghost"
                                        size="sm"
                                        className="text-destructive hover:text-destructive"
                                        onClick={() => remove(index)}
                                    >
                                        Remove
                                    </Button>
                                </div>
                            )}
                            <VolunteerBaseForm
                                fieldPrefix={`volunteers.${index}`}
                                hideActions
                                defaultServePeriod={defaultServePeriod}
                                defaultGroupOrganization={defaultGroupOrganization}
                            />
                            {fields.length > 1 && index < fields.length - 1 && (
                                <hr className="border-border" />
                            )}
                        </div>
                    ))}
                    <div className="flex flex-col gap-4">
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => append(makeDefaultEntry(defaultServePeriod, currentRegionId, defaultGroupOrganization))}
                            className="w-full"
                        >
                            <Plus className="h-4 w-4 mr-2" />
                            Add another volunteer
                        </Button>
                        <div className="grid grid-cols-1 gap-3 pt-2 min-w-0 md:grid-cols-[120px_1fr_1fr] md:gap-5 md:pt-4">
                            <DialogClose asChild>
                                <Button type="button" variant="outline" className="w-full shrink-0">
                                    Cancel
                                </Button>
                            </DialogClose>
                            <Button
                                type="button"
                                variant="outline"
                                className="w-full min-w-0 shrink-0"
                                onClick={() => form.reset()}
                            >
                                Reset
                            </Button>
                            <Button
                                className="w-full min-w-0 shrink-0 whitespace-normal text-center"
                                type="submit"
                                disabled={isSubmitting}
                                showLoader={isSubmitting}
                            >
                                {submitButtonLabel ?? `Add Volunteer${fields.length > 1 ? 's' : ''}`}
                            </Button>
                        </div>
                    </div>
                </div>
            </form>
        </FormProvider>
    )
}

export default CreateVolunteerForm
